import { createFileRoute } from '@tanstack/react-router'

import { EntryScreen } from '@/components/entry'
import { JellyIcon } from '@/components/icons'
import { WizardSummary } from '@/components/onboarding'
import { AppButton, Checkbox, SectionLabel } from '@/components/shared'
import { LEFT, SUMMARY } from '@/mocks/onboarding/hotovo'

export const Route = createFileRoute('/onboarding/hotovo')({
  head: () => ({ meta: [{ title: 'Hotovo · Aplikace Bitez' }] }),
  component: OnboardingHotovo,
})

/** Terms and privacy — buttons in the mock, documents in the real thing. */
function DocumentLink({ children }: { children: string }) {
  return (
    <button
      type="button"
      className="cursor-pointer font-bold text-ink underline underline-offset-2 transition-colors duration-150 hover:text-brand"
    >
      {children}
    </button>
  )
}

/**
 * The closing bookend. The wizard's steps live on the app's working cream, but
 * the way in (aktivace, vítejte) and the way out are photographs with a sheet
 * over them — you arrive through a room and you leave through it.
 *
 * Four things: proof the three minutes did something (the welcome's itinerary,
 * ticked), an honest word about what is still waiting on Přehled, the consent
 * that has to be given knowingly, and one door into the app.
 *
 * The leftovers are named as a **fact, not as homework** — the wizard only asked
 * what fits in a kitchen, so something is left by design, and hiding it here
 * would make the Přehled card feel like a nag that came out of nowhere. Payment
 * is still deliberately not mentioned: that one is said once, quietly, on
 * Přehled, and this screen is allowed to be a celebration.
 */
function OnboardingHotovo() {
  return (
    <EntryScreen sticker="star">
      <SectionLabel>Hotovo</SectionLabel>
      <h1 className="font-display mt-2 text-3xl leading-[0.88] uppercase">Můžeme začít</h1>
      <p className="mt-3 text-sm leading-relaxed text-ink/60">
        Bára se do toho pouští. První příspěvky uvidíte ke schválení během pár dní — a nic nevyjde,
        dokud je neodkliknete.
      </p>

      <section className="mt-7">
        <SectionLabel>Co už máme</SectionLabel>
        <div className="mt-4">
          <WizardSummary outcomes={SUMMARY} />
        </div>
      </section>

      {/* The honest half of the hand-off: the wizard only asked what fits in a
          kitchen, so something IS left — said once, quietly, as a fact rather
          than as homework. Payment is still deliberately not mentioned. */}
      <p className="mt-6 flex items-start gap-2.5 rounded-2xl bg-white p-4 text-[11px] leading-relaxed font-semibold text-ink/60 shadow-sm">
        <JellyIcon name="folder" className="mt-0.5 size-3.5 shrink-0 text-ink/40" />
        <span>
          Zbytek nastavení na vás počká na přehledu — začneme {LEFT.next}, ať má Bára z čeho stavět.
        </span>
      </p>

      <div aria-hidden className="my-7 border-t-2 border-dashed border-ink/10" />

      <Checkbox defaultChecked>
        Přečetl jsem si <DocumentLink>obchodní podmínky</DocumentLink> a{' '}
        <DocumentLink>zpracování osobních údajů</DocumentLink> a souhlasím s nimi.
      </Checkbox>

      <AppButton size="lg" href="/klient/prehled" className="mt-7 w-full">
        Vstoupit do aplikace
      </AppButton>
    </EntryScreen>
  )
}
